import { apiClient } from './apiClient';
import { indexedDbService } from './indexedDbService';
import { offlineService } from './offlineService';
import { cacheService } from './cacheService';
import { Job, JobType, Priority } from './jobService';

export interface Customer {
    id: string;  // Guid from backend
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    secondaryContactName?: string;
    secondaryContactPhone?: string;
    secondaryContactEmail?: string;
    source: CustomerSource;
    sourceDisplayName: string;
    firstContactDate: string;
    status: CustomerStatus | string;
    statusDisplayName: string;
    preferredCommunicationMethod?: string;
    notes?: string;
    createdDate: string;
    modifiedDate?: string;
    jobCount: number;
    totalJobValue: number;
    lifetimeValue: number;
    assignedSalesPersonId?: string;
    assignedSalesPersonName?: string;
    isDifficultPayer: boolean;
    difficultPayerNotes?: string;
}

export interface CreateCustomerDto {
    firstName: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    city?: string;
    country?: string;
    secondaryContactName?: string;
    secondaryContactPhone?: string;
    secondaryContactEmail?: string;
    source: CustomerSource;
    preferredCommunicationMethod?: string;
    notes?: string;
    assignedSalesPersonId?: string;
    isDifficultPayer?: boolean;
    difficultPayerNotes?: string;
}

export interface UpdateCustomerDto {
    id: string;  // Guid from backend
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    city?: string;
    country?: string;
    secondaryContactName?: string;
    secondaryContactPhone?: string;
    secondaryContactEmail?: string;
    source?: CustomerSource;
    status?: CustomerStatus;
    preferredCommunicationMethod?: string;
    notes?: string;
    assignedSalesPersonId?: string;
    isDifficultPayer?: boolean;
    difficultPayerNotes?: string;
}

export enum CustomerOrderBy {
    RecentActivity = 0,  // Most recently updated customers or customers with recent job activity
    Name = 1,            // Alphabetical by first name, then last name
    CreatedDate = 2      // Most recently created first
}

export interface CustomerSearchDto {
    searchTerm?: string;
    assignedSalesPersonId?: string;
    source?: CustomerSource;
    statuses?: CustomerStatus[]; // Multi-select status filter
    createdFrom?: string;
    createdTo?: string;
    page?: number;
    pageSize?: number;
    orderBy?: CustomerOrderBy;
}

export enum CustomerSource {
    Website = 1,
    Referral = 2,
    WalkIn = 3,
    Advertising = 4,
    SocialMedia = 5,
    EmailMarketing = 6,
    Other = 7,
    PreviousClient = 8,
    PhoneIn = 9
}

export enum CustomerStatus {
    Active = 1,
    Inactive = 2,
    Prospect = 3
}

export interface CreateCustomerWithJobDto extends CreateCustomerDto {
    createInitialJob?: boolean;
    initialJobType?: JobType;
    initialJobPriority?: Priority;
}

export interface CreateCustomerWithJobResponse {
    customer: Customer;
    initialJob?: Job;
}

class CustomerService {
    private static instance: CustomerService;
    private baseUrl = '/Customers';
    // In-flight request deduplication for getCustomer
    private customerPromises: Map<string, Promise<Customer>> = new Map();
    // Cache expiration time: 1 minute
    private readonly CACHE_EXPIRATION = 60 * 1000;
    // Deduplication window: 2 seconds (longer than React StrictMode remount)
    private readonly DEDUP_WINDOW = 2000;

    public static getInstance(): CustomerService {
        if (!CustomerService.instance) {
            CustomerService.instance = new CustomerService();
        }
        return CustomerService.instance;
    }

    async getCustomers(searchDto?: CustomerSearchDto): Promise<Customer[]> {
        try {
            // Check if we're offline
            const isOffline = !offlineService.getOnlineStatus();
            
            if (isOffline) {
                console.log('Offline mode: Getting customers from IndexedDB');
                // Get current user to determine business ID
                const currentUser = await indexedDbService.getCurrentUser();
                if (!currentUser) {
                    console.error('No current user found in offline mode');
                    return [];
                }
                
                const customers = await indexedDbService.getCustomersForBusiness(currentUser.businessId);
                
                // Apply basic filtering if search criteria provided
                if (searchDto && searchDto.searchTerm) {
                    const searchTerm = searchDto.searchTerm.toLowerCase();
                    return customers.filter(customer => 
                        customer.firstName?.toLowerCase().includes(searchTerm) ||
                        customer.lastName?.toLowerCase().includes(searchTerm) ||
                        customer.email?.toLowerCase().includes(searchTerm) ||
                        customer.phoneNumber?.includes(searchTerm)
                    );
                }
                
                return customers;
            }
            
            if (searchDto && searchDto.statuses && searchDto.statuses.length > 0) {
                // Use POST for complex search with arrays
                const response = await apiClient.post<Customer[]>(`${this.baseUrl}/search`, searchDto);
                return response.data;
            } else {
                // Use GET for simple searches
                const params = new URLSearchParams();
                if (searchDto) {
                    Object.entries(searchDto).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '') {
                            params.append(key, value.toString());
                        }
                    });
                }

                const response = await apiClient.get<Customer[]>(`${this.baseUrl}?${params.toString()}`);
                return response.data;
            }
        } catch (error: any) {
            console.error('Error fetching customers:', error);
            throw error;
        }
    }

    async getCustomer(id: string): Promise<Customer> {
        const cacheKey = `customer:${id}`;

        try {
            // Check if we're offline
            const isOffline = !offlineService.getOnlineStatus();

            if (isOffline) {
                console.log('Offline mode: Getting customer from IndexedDB');
                const customer = await indexedDbService.getCustomer(id);
                if (customer) {
                    return customer;
                } else {
                    throw new Error(`Customer ${id} not found in offline storage`);
                }
            }

            // Check in-flight request deduplication
            const existingPromise = this.customerPromises.get(id);
            if (existingPromise) {
                console.log(`Customer ${id}: Returning existing in-flight request`);
                return existingPromise;
            }

            // Check memory cache
            const cached = cacheService.get<Customer>(cacheKey);
            if (cached) {
                console.log(`Customer ${id}: Cache hit`);
                return cached;
            }

            // Create new request with deduplication
            const promise = (async () => {
                const response = await apiClient.get<Customer>(`${this.baseUrl}/${id}`);
                // Cache the result
                cacheService.set(cacheKey, response.data, this.CACHE_EXPIRATION);
                return response.data;
            })();

            // Store the promise for deduplication
            this.customerPromises.set(id, promise);

            try {
                const result = await promise;
                return result;
            } finally {
                // Clear the promise after deduplication window
                setTimeout(() => {
                    this.customerPromises.delete(id);
                }, this.DEDUP_WINDOW);
            }
        } catch (error: any) {
            console.error('Error fetching customer:', error);
            // Clear from dedup map on error
            this.customerPromises.delete(id);
            throw error;
        }
    }

    async getCustomersBySalesPerson(salesPersonId: string): Promise<Customer[]> {
        try {
            const response = await apiClient.get<Customer[]>(`${this.baseUrl}/assigned/${salesPersonId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching customers by sales person:', error);
            throw error;
        }
    }

    async createCustomer(customer: CreateCustomerDto): Promise<Customer> {
        try {
            const response = await apiClient.post<Customer>(this.baseUrl, customer);
            return response.data;
        } catch (error: any) {
            console.error('Error creating customer:', error);
            throw error;
        }
    }

    async createCustomerWithJob(dto: CreateCustomerWithJobDto): Promise<CreateCustomerWithJobResponse> {
        try {
            const response = await apiClient.post<CreateCustomerWithJobResponse>(`${this.baseUrl}/with-initial-job`, dto);
            return response.data;
        } catch (error: any) {
            console.error('Error creating customer with job:', error);
            throw error;
        }
    }

    async updateCustomer(customer: UpdateCustomerDto): Promise<Customer> {
        try {
            const response = await apiClient.put<Customer>(`${this.baseUrl}/${customer.id}`, customer);
            return response.data;
        } catch (error: any) {
            console.error('Error updating customer:', error);
            throw error;
        }
    }

    async deleteCustomer(id: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/${id}`);
        } catch (error: any) {
            console.error('Error deleting customer:', error);
            throw error;
        }
    }

    async searchCustomers(searchDto: CustomerSearchDto): Promise<Customer[]> {
        try {
            const response = await apiClient.post<Customer[]>(`${this.baseUrl}/search`, searchDto);
            return response.data;
        } catch (error: any) {
            console.error('Error searching customers:', error);
            throw error;
        }
    }

    async getCustomerCount(searchDto?: CustomerSearchDto): Promise<number> {
        try {
            if (searchDto && searchDto.statuses && searchDto.statuses.length > 0) {
                // Use POST for complex search with arrays
                const response = await apiClient.post<number>(`${this.baseUrl}/count`, searchDto);
                return response.data;
            } else {
                // Use GET for simple searches
                const params = new URLSearchParams();
                if (searchDto) {
                    Object.entries(searchDto).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '' && value !== 0) {
                            params.append(key, value.toString());
                        }
                    });
                }

                const response = await apiClient.get<number>(`${this.baseUrl}/count?${params.toString()}`);
                return response.data;
            }
        } catch (error: any) {
            console.error('Error getting customer count:', error);
            throw error;
        }
    }

    // Helper methods for display
    getSourceDisplayName(source: CustomerSource): string {
        const sourceNames = {
            [CustomerSource.Website]: 'Website',
            [CustomerSource.Referral]: 'Referral',
            [CustomerSource.WalkIn]: 'Walk In',
            [CustomerSource.Advertising]: 'Advertising',
            [CustomerSource.SocialMedia]: 'Social Media',
            [CustomerSource.EmailMarketing]: 'Email Marketing',
            [CustomerSource.Other]: 'Other',
            [CustomerSource.PreviousClient]: 'Previous Client',
            [CustomerSource.PhoneIn]: 'Phone In'
        };
        return sourceNames[source] || 'Unknown';
    }

    getStatusDisplayName(status: CustomerStatus): string {
        const statusNames = {
            [CustomerStatus.Active]: 'Active',
            [CustomerStatus.Inactive]: 'Inactive',
            [CustomerStatus.Prospect]: 'Prospect'
        };
        return statusNames[status] || 'Unknown';
    }

    getStatusColor(status: CustomerStatus): string {
        const statusColors = {
            [CustomerStatus.Active]: '#4CAF50',
            [CustomerStatus.Inactive]: '#757575',
            [CustomerStatus.Prospect]: '#FF9800'
        };
        return statusColors[status] || '#757575';
    }

    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }
}

export const customerService = CustomerService.getInstance();
